import React, { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import restaurantService from '../services/restaurantService'
import ingredientsService from '../services/ingredientsService'
import { Restaurant } from '../types/restaurant'
import type {
  MyIngredientItem,
  IngredientCatalogItem,
  IngredientRelatedItem,
  CreateCustomIngredientData,
} from '../types/ingredient'
import toast from 'react-hot-toast'
import { useTranslation } from '../hooks/use-i18n'

const RestaurantIngredients: React.FC = () => {
  const { t } = useTranslation()
  const { restaurantId } = useParams<{ restaurantId: string }>()
  const [restaurant, setRestaurant] = useState<Restaurant | null>(null)
  const [myIngredients, setMyIngredients] = useState<MyIngredientItem[]>([])
  const [relatedIngredients, setRelatedIngredients] = useState<IngredientRelatedItem[]>([])
  const [allCatalogIngredients, setAllCatalogIngredients] = useState<IngredientCatalogItem[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [showAllCatalog, setShowAllCatalog] = useState(false)
  const [customName, setCustomName] = useState('')
  const [customDescription, setCustomDescription] = useState('')
  const [customPrice, setCustomPrice] = useState('')
  const [customUnit, setCustomUnit] = useState('')
  const [customCurrency, setCustomCurrency] = useState('')
  const [customSubmitting, setCustomSubmitting] = useState(false)
  const [editingCustomId, setEditingCustomId] = useState<string | null>(null)
  const [editCustomName, setEditCustomName] = useState('')
  const [editCustomDescription, setEditCustomDescription] = useState('')
  const [editCustomPrice, setEditCustomPrice] = useState('')
  const [editCustomUnit, setEditCustomUnit] = useState('')
  const [editCustomCurrency, setEditCustomCurrency] = useState('')
  const [addLoadingId, setAddLoadingId] = useState<string | null>(null)
  const [removeLoadingId, setRemoveLoadingId] = useState<string | null>(null)

  const loadRestaurantAndMy = async () => {
    if (!restaurantId) return
    try {
      const [rest, my] = await Promise.all([
        restaurantService.getRestaurant(restaurantId),
        ingredientsService.getMyIngredients(restaurantId),
      ])
      setRestaurant(rest)
      setMyIngredients(my)
    } catch (e) {
      throw e
    }
  }

  const loadRelated = async () => {
    if (!restaurantId) return
    const data = await ingredientsService.getRelated(restaurantId)
    setRelatedIngredients(data)
  }

  const loadAllCatalog = async () => {
    const data = await ingredientsService.getCatalog()
    setAllCatalogIngredients(data)
  }

  useEffect(() => {
    const load = async () => {
      if (!restaurantId) {
        setError(t('error.restaurantIdMissing'))
        setIsLoading(false)
        return
      }
      try {
        setIsLoading(true)
        await loadRestaurantAndMy()
        await loadRelated()
      } catch (err: any) {
        setError(err.response?.data?.message || t('error.ingredientsLoadFailed'))
        toast.error(t('toast.ingredientsLoadFailed'))
      } finally {
        setIsLoading(false)
      }
    }
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run only when restaurantId changes
  }, [restaurantId])

  useEffect(() => {
    if (showAllCatalog && restaurantId) {
      loadAllCatalog()
    }
  }, [showAllCatalog, restaurantId])

  const myCatalogIds = new Set(
    myIngredients
      .filter((i) => i.source === 'catalog' && (i.catalogIngredientId || i.id))
      .map((i) => i.catalogIngredientId || i.id)
  )

  const handleAddCatalog = async (ingredientId: string) => {
    if (!restaurantId) return
    setAddLoadingId(ingredientId)
    try {
      await ingredientsService.addCatalogToRestaurant(restaurantId, ingredientId)
      await loadRestaurantAndMy()
      if (!showAllCatalog) await loadRelated()
      toast.success(t('toast.addedToMyIngredients'))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.addIngredientFailed'))
    } finally {
      setAddLoadingId(null)
    }
  }

  const handleRemoveCatalog = async (ingredientId: string) => {
    if (!restaurantId) return
    setRemoveLoadingId(ingredientId)
    try {
      await ingredientsService.removeCatalogFromRestaurant(restaurantId, ingredientId)
      await loadRestaurantAndMy()
      if (!showAllCatalog) await loadRelated()
      toast.success(t('toast.removedFromMyIngredients'))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.removeIngredientFailed'))
    } finally {
      setRemoveLoadingId(null)
    }
  }

  const handleAddCustom = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!restaurantId || !customName.trim()) {
      toast.error(t('toast.nameRequired'))
      return
    }
    setCustomSubmitting(true)
    try {
      const data: CreateCustomIngredientData = {
        name: customName.trim(),
        description: customDescription.trim() || undefined,
        price: customPrice ? parseFloat(customPrice) : undefined,
        unitOfMeasure: customUnit.trim() || undefined,
        currency: customCurrency.trim() || undefined,
      }
      await ingredientsService.createCustomIngredient(restaurantId, data)
      await loadRestaurantAndMy()
      setCustomName('')
      setCustomDescription('')
      setCustomPrice('')
      setCustomUnit('')
      setCustomCurrency('')
      toast.success(t('toast.customIngredientAdded'))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.customIngredientFailed'))
    } finally {
      setCustomSubmitting(false)
    }
  }

  const handleStartEditCustom = (item: MyIngredientItem) => {
    if (item.source !== 'custom' || !item.customIngredientId) return
    setEditingCustomId(item.customIngredientId)
    setEditCustomName(item.name)
    setEditCustomDescription(item.description || '')
    setEditCustomPrice(item.price || '')
    setEditCustomUnit(item.unitOfMeasure || '')
    setEditCustomCurrency(item.currency || '')
  }

  const handleSaveEditCustom = async () => {
    if (!restaurantId || !editingCustomId) return
    try {
      await ingredientsService.updateCustomIngredient(
        restaurantId,
        editingCustomId,
        {
          name: editCustomName.trim(),
          description: editCustomDescription.trim() || undefined,
          price: editCustomPrice ? parseFloat(editCustomPrice) : undefined,
          unitOfMeasure: editCustomUnit.trim() || undefined,
          currency: editCustomCurrency.trim() || undefined,
        }
      )
      await loadRestaurantAndMy()
      setEditingCustomId(null)
      toast.success(t('toast.updated'))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.updateFailed'))
    }
  }

  const handleDeleteCustom = async (customId: string) => {
    if (!restaurantId || !window.confirm(t('common.deleteCustomIngredientConfirm'))) return
    try {
      await ingredientsService.deleteCustomIngredient(restaurantId, customId)
      await loadRestaurantAndMy()
      setEditingCustomId(null)
      toast.success(t('toast.deleted'))
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('toast.deleteFailed'))
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-500" />
      </div>
    )
  }

  if (error || !restaurant) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="bg-[#2a2a2a] border border-white/10 rounded-3xl p-8 max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-white mb-4">{t('ingredient.notFound.title')}</h2>
          <p className="text-white/80 mb-6">{error || t('ingredient.notFound.defaultMessage')}</p>
          <Link
            to="/dashboard"
            className="inline-block bg-black hover:bg-black/80 text-white px-6 py-3 rounded-full font-semibold transition-all duration-300"
          >
            {t('common.backToDashboard')}
          </Link>
        </div>
      </div>
    )
  }

  type CatalogRow = { id: string; name: string; price?: string; unitOfMeasure?: string; currency?: string; inMyList: boolean }
  const catalogList: CatalogRow[] = showAllCatalog
    ? allCatalogIngredients.map((i) => ({
        ...i,
        inMyList: myCatalogIds.has(i.id),
      }))
    : relatedIngredients.map((i) => ({ ...i, inMyList: i.inMyList }))

  return (
    <div className="min-h-screen">
      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-6 lg:py-8">
        <Link
          to={`/restaurant/${restaurantId}`}
          className="inline-flex items-center text-white/80 hover:text-white mb-6 transition-colors"
        >
          {t('common.backWithName', { name: restaurant.name })}
        </Link>
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2">{t('ingredient.myList.title')}</h1>
        <p className="text-white/70 text-sm mb-8">
          {t('ingredient.subtitle')}
        </p>

        {/* My ingredients list */}
        <section className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 mb-8">
          <h2 className="text-xl font-bold text-white mb-4">{t('ingredient.myList.title')}</h2>
          {myIngredients.length === 0 ? (
            <p className="text-white/60">{t('ingredient.myList.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.name')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.description')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.price')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.unit')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.currency')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.source')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4 w-32">{t('ingredient.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {myIngredients.map((item) => (
                    <tr key={item.source === 'custom' ? item.customIngredientId : item.catalogIngredientId || item.id} className="border-b border-white/10 hover:bg-white/5">
                      <td className="py-3 pr-4 font-medium text-white">{item.name}</td>
                      <td className="py-3 pr-4 text-white/90 text-sm">{item.description || '—'}</td>
                      <td className="py-3 pr-4 text-white/90">{item.price ?? '—'}</td>
                      <td className="py-3 pr-4 text-white/90">{item.unitOfMeasure ?? '—'}</td>
                      <td className="py-3 pr-4 text-white/90">{item.currency ?? '—'}</td>
                      <td className="py-3 pr-4">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${item.source === 'catalog' ? 'bg-sky-500/20 text-sky-300' : 'bg-amber-500/20 text-amber-300'}`}>
                          {item.source}
                        </span>
                      </td>
                      <td className="py-3 pr-4">
                        {item.source === 'catalog' && (item.catalogIngredientId || item.id) && (
                          <button
                            type="button"
                            disabled={removeLoadingId !== null}
                            onClick={() => handleRemoveCatalog(item.catalogIngredientId || item.id)}
                            className="text-red-400 hover:text-red-300 text-sm font-medium disabled:opacity-50"
                          >
                            {removeLoadingId === (item.catalogIngredientId || item.id) ? t('common.ellipsis') : t('common.remove')}
                          </button>
                        )}
                        {item.source === 'custom' && item.customIngredientId && (
                          <>
                            <button
                              type="button"
                              onClick={() => handleStartEditCustom(item)}
                              className="text-sky-400 hover:text-sky-300 text-sm font-medium mr-3"
                            >
                              {t('common.edit')}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteCustom(item.customIngredientId!)}
                              className="text-red-400 hover:text-red-300 text-sm font-medium"
                            >
                              {t('common.delete')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Edit custom modal */}
        {editingCustomId && (
          <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
            <div className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 max-w-md w-full">
              <h3 className="text-lg font-bold text-white mb-4">{t('ingredient.edit.title')}</h3>
              <div className="space-y-3">
                <input
                  type="text"
                  value={editCustomName}
                  onChange={(e) => setEditCustomName(e.target.value)}
                  placeholder={t('ingredient.edit.namePlaceholder')}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
                />
                <input
                  type="text"
                  value={editCustomDescription}
                  onChange={(e) => setEditCustomDescription(e.target.value)}
                  placeholder={t('ingredient.edit.descriptionPlaceholder')}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
                />
                <input
                  type="number"
                  step="0.01"
                  value={editCustomPrice}
                  onChange={(e) => setEditCustomPrice(e.target.value)}
                  placeholder={t('ingredient.edit.pricePlaceholder')}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
                />
                <input
                  type="text"
                  value={editCustomUnit}
                  onChange={(e) => setEditCustomUnit(e.target.value)}
                  placeholder={t('ingredient.edit.unitPlaceholder')}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
                />
                <input
                  type="text"
                  value={editCustomCurrency}
                  onChange={(e) => setEditCustomCurrency(e.target.value)}
                  placeholder={t('ingredient.edit.currencyPlaceholder')}
                  className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
                />
              </div>
              <div className="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setEditingCustomId(null)}
                  className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleSaveEditCustom}
                  className="px-4 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600"
                >
                  {t('common.save')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add from catalog */}
        <section className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6 mb-8">
          <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
            <h2 className="text-xl font-bold text-white">
              {showAllCatalog ? t('ingredient.catalog.allTitle') : t('ingredient.catalog.relatedTitle')}
            </h2>
            <button
              type="button"
              onClick={() => setShowAllCatalog((v) => !v)}
              className="px-4 py-2 rounded-lg bg-white/10 text-white hover:bg-white/20 text-sm font-medium"
            >
              {showAllCatalog ? t('ingredient.catalog.showRelated') : t('ingredient.catalog.showAll')}
            </button>
          </div>
          {!showAllCatalog && (
            <p className="text-white/60 text-sm mb-4">
              {t('ingredient.catalog.relatedHint')}
            </p>
          )}
          {catalogList.length === 0 ? (
            <p className="text-white/60">
              {showAllCatalog ? t('ingredient.catalog.emptyAll') : t('ingredient.catalog.emptyRelated')}
            </p>
          ) : (
            <div className="overflow-x-auto max-h-80 overflow-y-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.name')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.price')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.unit')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4">{t('ingredient.table.currency')}</th>
                    <th className="text-white/80 text-sm font-semibold py-3 pr-4 w-28">{t('ingredient.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {catalogList.map((item) => {
                    const inMyList = item.inMyList
                    return (
                      <tr key={item.id} className="border-b border-white/10 hover:bg-white/5">
                        <td className="py-3 pr-4 font-medium text-white">{item.name}</td>
                        <td className="py-3 pr-4 text-white/90">{item.price ?? '—'}</td>
                        <td className="py-3 pr-4 text-white/90">{item.unitOfMeasure ?? '—'}</td>
                        <td className="py-3 pr-4 text-white/90">{item.currency ?? '—'}</td>
                        <td className="py-3 pr-4">
                          {inMyList ? (
                            <span className="text-white/50 text-sm">{t('ingredient.catalog.inMyList')}</span>
                          ) : (
                            <button
                              type="button"
                              disabled={addLoadingId !== null}
                              onClick={() => handleAddCatalog(item.id)}
                              className="text-sky-400 hover:text-sky-300 text-sm font-medium disabled:opacity-50"
                            >
                              {addLoadingId === item.id ? t('common.ellipsis') : t('common.addToMyIngredients')}
                            </button>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Add custom ingredient */}
        <section className="bg-[#2a2a2a] border border-white/10 rounded-2xl p-6">
          <h2 className="text-xl font-bold text-white mb-4">{t('ingredient.custom.addTitle')}</h2>
          <p className="text-white/60 text-sm mb-4">
            {t('ingredient.custom.addHint')}
          </p>
          <form onSubmit={handleAddCustom} className="space-y-3 max-w-xl">
            <input
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              placeholder={t('ingredient.custom.namePlaceholder')}
              required
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
            />
            <input
              type="text"
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder={t('ingredient.custom.descriptionPlaceholder')}
              className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <input
                type="number"
                step="0.01"
                value={customPrice}
                onChange={(e) => setCustomPrice(e.target.value)}
                placeholder={t('ingredient.custom.pricePlaceholder')}
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
              />
              <input
                type="text"
                value={customUnit}
                onChange={(e) => setCustomUnit(e.target.value)}
                placeholder={t('ingredient.custom.unitPlaceholder')}
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
              />
              <input
                type="text"
                value={customCurrency}
                onChange={(e) => setCustomCurrency(e.target.value)}
                placeholder={t('ingredient.custom.currencyPlaceholder')}
                className="w-full px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50"
              />
            </div>
            <button
              type="submit"
              disabled={customSubmitting || !customName.trim()}
              className="px-6 py-2 rounded-lg bg-sky-500 text-white hover:bg-sky-600 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {customSubmitting ? t('ingredient.custom.submitting') : t('ingredient.custom.submit')}
            </button>
          </form>
        </section>
      </main>
    </div>
  )
}

export default RestaurantIngredients
